import React, { useCallback } from 'react';
import { Box, costFormater, MultiLineChart, Text } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { FULL_MONTH_FORMAT } from '@/common/constants';

import EmployerSectionPlaceholder from '../EmployerSectionPlaceholder';

import { EmployerClaimSpendPreloader, EmployerSpendsTitle } from './components';
import { useGetBalanceSpendQuery } from './queries';

interface Props {
  planId: string;
}
const EmployerBalanceSpendContainer: React.FC<Props> = ({ planId }) => {
  const {
    isLoading,
    isError,
    datasets,
    labels,
    totalValues,
    dates,
    delimiters,
    currentDateIndex,
    refetch,
    isSuccess,
  } = useGetBalanceSpendQuery(planId);

  const getCurrentHintData = useCallback(
    (index: number) => {
      return (
        <MultiLineChart.Tooltip>
          <Box direction="column">
            <Text size="small" weight={700} color="textOnColor">
              {dayjs(dates[index]).format(FULL_MONTH_FORMAT)}
            </Text>
            {datasets.map((item, dataSetIndex) => {
              if (item.values[index] || item.values[index] === 0) {
                return (
                  <Box align="center" direction="row" key={`hint-indicator_${index}_${dataSetIndex}`}>
                    <Box width="8px" height="8px" round="smallRound" background={item.color}></Box>
                    <Text margin={{ left: 'spacing4' }} color="textOnColor" size="small" weight={500}>
                      {item.label}:{' '}
                      {costFormater(item.values[index], {
                        isPrecision: true,
                      })}
                    </Text>
                  </Box>
                );
              }
              return null;
            })}
          </Box>
        </MultiLineChart.Tooltip>
      );
    },
    [datasets, dates]
  );

  if (isLoading || (!isSuccess && !isError)) {
    return <EmployerClaimSpendPreloader isHSA isLoading />;
  }
  if (isError) {
    return (
      <EmployerSectionPlaceholder onTryAgain={refetch}>
        We couldn't load your balance data
      </EmployerSectionPlaceholder>
    );
  }
  return (
    <Box
      pad="spacing24"
      background="canvas"
      round="container2Round"
      border={{ size: '1px', color: 'border2' }}
      data-testid="employer-balance-spend"
    >
      <Text size="medium" weight={700} data-testid="employer-balance-spend_title">
        Balance
      </Text>
      <Box direction="row" height="100%" margin={{ top: 'spacing12' }}>
        <Box width="190px">
          <EmployerSpendsTitle
            cashValue={totalValues.cashBalance}
            investmentsValue={totalValues.investmentsBalance}
          />
        </Box>
        <Box fill direction="row" align="end" height="100%" margin={{ left: 'spacing32' }}>
          <MultiLineChart
            currentIndex={currentDateIndex}
            datasets={datasets}
            labels={labels}
            delimiters={delimiters}
            height={176}
            renderTooltip={(data) => getCurrentHintData(data.index)}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default EmployerBalanceSpendContainer;
